import React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useAuth } from '../../hooks/useAuth';
import { UserPersona } from '../../types/cabinet.types';
import { NavTab } from './BottomNavbar';

import HeartHandshakeIcon from '@/assets/image/heart-handshake.svg?react';
import CalendarDaysIcon from '@/assets/image/calendar-days.svg?react';
import CalendarPlus from '@/assets/image/calendar-plus.svg?react';
import FileTextIcon from '@/assets/image/file-text.svg?react';
import UserCogIcon from '@/assets/image/user-cog.svg?react';

interface DesktopSidebarProps {
  activeTab: NavTab;
  onChangeTab: (tab: NavTab) => void;
  persona: UserPersona;
  isWeightLoss?: boolean;
}

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({ 
  activeTab, 
  onChangeTab, 
  persona, 
  isWeightLoss 
}) => {
  const { t } = useI18n();
  const { logout } = useAuth();

  const isPersonaA = persona === 'A' || isWeightLoss === true;

  const tabs = isPersonaA
    ? [
        { id: 'therapy', label: t('nav_terapia'), icon: <HeartHandshakeIcon /> },
        { id: 'services', label: t('nav_uslugi'), icon: <CalendarDaysIcon /> },
        { id: 'documents', label: t('nav_doc'), icon: <FileTextIcon /> },
        { id: 'profile', label: t('nav_dane'), icon: <UserCogIcon /> }
      ] as const
    : [
        { id: 'services', label: t('nav_umow'), icon: <CalendarPlus /> },
        { id: 'consultations', label: t('nav_cons'), icon: <HeartHandshakeIcon /> },
        { id: 'documents', label: t('nav_doc'), icon: <FileTextIcon /> },
        { id: 'profile', label: t('nav_dane'), icon: <UserCogIcon /> }
      ] as const;

  return (
    <aside className="cab-desktop-sidebar">
      <div className="cab-sidebar-nav">
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.id || (tab.id === 'therapy' && (activeTab === 'visits' || activeTab === 'visit-summary')) || (tab.id === 'consultations' && activeTab === 'visit-summary');
          return (
            <button
              key={tab.id}
              className={`cab-sidebar-btn ${isActive ? 'active' : ''}`}
              onClick={() => onChangeTab(tab.id as NavTab)}
              data-tour={`nav-${index + 1}`}
            >
              <span className="cab-sidebar-icon">{tab.icon}</span>
              <span className="cab-sidebar-label">{tab.label}</span>
            </button>
          );
        })}
      </div>
      
      <div className="cab-sidebar-footer">
        <button className="cab-sidebar-btn cab-sidebar-logout" onClick={logout}>
          <span className="cab-sidebar-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </span>
          <span className="cab-sidebar-label">{t('logout')}</span>
        </button>
      </div>

      <style>{`
        .cab-desktop-sidebar {
          display: none; /* Hidden on mobile */
          width: 260px;
          flex-shrink: 0;
          background: #E6D6FA;
          border-radius: 24px;
          padding: 32px 20px;
          flex-direction: column;
          justify-content: space-between;
          height: calc(100vh - 64px);
          max-height: 640px;
          position: sticky;
          top: 32px;
        }
        .cab-sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .cab-sidebar-btn {
          display: flex;
          align-items: center;
          gap: 16px;
          width: 100%;
          padding: 14px 20px;
          border: none;
          background: transparent;
          border-radius: 52px;
          cursor: pointer;
          color: #2F116A;
          font-size: 15px;
          font-weight: 500;
          transition: background-color 0.2s, color 0.2s;
        }
        .cab-sidebar-btn:hover {
          background: rgba(255, 255, 255, 0.4);
        }
        .cab-sidebar-btn.active {
          background: #FFFFFF;
          box-shadow: 0px 4px 12px rgba(54, 23, 119, 0.03);
        }
        .cab-sidebar-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }
        .cab-sidebar-icon svg {
          width: 100%;
          height: 100%;
        }
        .cab-sidebar-icon svg path,
        .cab-sidebar-icon svg rect,
        .cab-sidebar-icon svg line,
        .cab-sidebar-icon svg polyline,
        .cab-sidebar-icon svg circle {
          stroke: currentColor;
        }
        .cab-sidebar-logout {
          margin-top: auto;
          background: #FFFFFF;
          font-weight: 600;
        }
        
        @media (min-width: 1024px) {
          .cab-desktop-sidebar {
            display: flex;
          }
        }
      `}</style>
    </aside>
  );
};
